import { publicPinnedTrophyNullableSchema } from '@crowdfpl/domain/gamification';
import { z } from 'zod';

export type LeaderboardScope = 'duels' | 'captain' | 'ai';

const leaderboardScopeSchema = z.enum(['duels', 'captain', 'ai']);

const PeakPointSchema = z.object({
  value: z.number(),
  gameweek_id: z.string().uuid(),
  gameweek_number: z.number().int(),
  season_id: z.string().uuid().nullable().optional(),
  season: z.string().nullable().optional(),
});

const captainRatingField = z.coerce.number().catch(1000);
const captainCountField = z.coerce.number().catch(0);

export const ProfileSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  username: z.string(),
  avatar_url: z.string().url().nullable(),
  twitter_username: z.string().optional().nullable(),
  show_twitter_username: z.boolean().optional(),
  fpl_id: z.string().nullable(),
  predictor_rating: z.number(),
  success_rate: z.number(),
  total_predictions: z.number(),
  current_streak: z.number(),
  rank: z.number().optional().nullable(),
  country_rank: z.number().optional().nullable(),
  captain_rank: z.number().optional().nullable(),
  previous_rank: z.union([
    z.number(),
    z.object({
      global: z.number().nullable().optional(),
      country: z.number().nullable().optional(),
      captain: z.number().nullable().optional(),
    }),
  ]).optional().nullable(),
  rank_movement: z.union([
    z.number(),
    z.object({
      global: z.number().nullable().optional(),
      country: z.number().nullable().optional(),
      captain: z.number().nullable().optional(),
    }),
  ]).optional().nullable(),
  best_week: z.object({
    global: z.object({
      rank: z.number(),
      gameweek_id: z.string().uuid(),
      gameweek_number: z.number().int(),
      season_id: z.string().uuid().nullable().optional(),
      season: z.string().nullable().optional(),
    }).nullable().optional(),
    country: z.object({
      rank: z.number(),
      gameweek_id: z.string().uuid(),
      gameweek_number: z.number().int(),
      season_id: z.string().uuid().nullable().optional(),
      season: z.string().nullable().optional(),
    }).nullable().optional(),
  }).optional().nullable(),
  peak_points: z.object({
    rating: z.object({
      all_time: PeakPointSchema.nullable().optional(),
      current_season: PeakPointSchema.nullable().optional(),
    }).optional(),
    gw_delta: z.object({
      all_time: PeakPointSchema.nullable().optional(),
      current_season: PeakPointSchema.nullable().optional(),
    }).optional(),
  }).optional().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // New fields for email verification
  email: z.string().email().optional().nullable(),
  email_verified: z.boolean().optional(),
  auth_provider: z.string().optional().nullable(),
  has_password: z.boolean().optional(),
  linked_providers: z.array(z.string()).optional(),
  linked_providers_detail: z
    .array(
      z.object({
        provider: z.string(),
        provider_email: z.string().nullable().optional(),
        provider_username: z.string().nullable().optional(),
      }),
    )
    .optional(),
  // Country (ISO 3166-1 alpha-2) — user-set; API may return "" when not set
  country: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  ),
  // Request country (auto-detected from request, e.g. /me)
  request_country: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().length(2).regex(/^[A-Z]{2}$/).optional().nullable(),
  ),
  // UI flags (modal shown state, etc.)
  ui_flags: z
    .object({
      follow_x_modal_shown: z.boolean().optional(),
      country_modal_shown: z.boolean().optional(),
      'revote.confirm.earlyBonus': z.boolean().optional(),
      'revote.confirm.final': z.boolean().optional(),
      'revote.confirm.finalWithEarlyBonus': z.boolean().optional(),
    })
    .optional(),
  /** AI/LLM bot account — show LLM leaderboard instead of global/country on profile. */
  is_bot: z.boolean().optional(),
  /** All-time rank on the LLM leaderboard (bots only). */
  llm_leaderboard_rank: z.number().int().nullable().optional(),
  captain_rating: captainRatingField,
  prev_captain_rating: captainRatingField,
  captain_total_predictions: captainCountField,
  captain_success_count: captainCountField,
  captain_current_streak: captainCountField,
  pinned_trophy: publicPinnedTrophyNullableSchema.optional(),
});

export const ProfilesListResponseSchema = z.object({
  profiles: z.array(ProfileSchema),
  pagination: z.object({
    limit: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().optional(),
  }),
});

// Leaderboard entry schema (duels + captain scopes share shape; scope-specific fields optional)
export const LeaderboardEntrySchema = z.object({
  user_id: z.string().uuid(),
  username: z.string(),
  predictor_rating: z.number().optional(),
  rank: z.number(),
  avatar_url: z.string().nullable().optional(),
  fpl_id: z.string().nullable().optional(),
  success_rate: z.number().optional(),
  total_predictions: z.number().optional(),
  country: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  ),
  twitter_username: z.string().nullable().optional(),
  show_twitter_username: z.boolean().optional(),
  // GW-specific fields (when gameweek_id is provided)
  rating_change: z.number().optional(),
  votes_in_gw: z.number().optional(),
  success_rate_gw: z.number().optional(),
  // Scope-aware optional fields
  rank_movement: z.number().optional(),
  previous_rank: z.number().nullable().optional(),
  pts_to_leader: z.number().optional(),
  // Captain scope fields (scope=captain)
  captain_rating: z.number().optional(),
  prev_captain_rating: z.number().optional(),
  captain_hit_rate: z.number().optional(),
  captains_picked: z.number().optional(),
  captain_streak: z.number().optional(),
  captain_top_pick_count: z.number().int().nonnegative().optional(),
  captain_valid_gws: z.number().int().nonnegative().optional(),
  captain_success_pct: z.number().int().min(0).max(100).nullable().optional(),
  pinned_trophy: publicPinnedTrophyNullableSchema.optional(),
});

export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>;

export const LeaderboardScopeCardSchema = z.object({
  rank: z.number().nullable().optional(),
  managers_count: z.number().int().nonnegative().nullable().optional(),
  pts_to_leader: z.number().nullable().optional(),
  previous_rank: z.number().nullable().optional(),
  rank_movement: z.number().nullable().optional(),
  /** API returns "League Leader" | "Top 10" | null */
  badge: z.string().nullable().optional(),
  country_code: z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  country_name: z.string().nullable().optional(),
});

export const LeaderboardAiScopeCardSchema = z.object({
  models_count: z.number().int().nonnegative(),
});

export const LeaderboardScopeCardsSchema = z.object({
  duels: LeaderboardScopeCardSchema.optional(),
  captain: LeaderboardScopeCardSchema.optional(),
  /** Omitted on older API responses; UI treats missing as “no models count yet”. */
  ai: LeaderboardAiScopeCardSchema.optional(),
});

export type LeaderboardScopeCard = z.infer<typeof LeaderboardScopeCardSchema>;
export type LeaderboardScopeCards = z.infer<typeof LeaderboardScopeCardsSchema>;

export const LeaderboardResponseSchema = z.object({
  leaderboard: z.array(LeaderboardEntrySchema),
  pagination: z.object({
    limit: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().optional(),
  }),
  // Current user's position in GW (when authorized + gameweek_id; null if guest or no votes in GW)
  my_position: LeaderboardEntrySchema.optional().nullable(),
  // Optional scope-aware metadata (new backend contract, backward compatible)
  scope: leaderboardScopeSchema.optional(),
  scope_cards: LeaderboardScopeCardsSchema.optional(),
  country_required_for_scope: z.boolean().optional(),
});

export type ProfileDto = z.infer<typeof ProfileSchema>;
export type ProfilesListResponse = z.infer<typeof ProfilesListResponseSchema>;
export type LeaderboardResponse = z.infer<typeof LeaderboardResponseSchema>;

// My Profile endpoint
export const MyProfileResponseSchema = z.object({
  success: z.literal(true),
  data: ProfileSchema,
});
export type MyProfileResponse = z.infer<typeof MyProfileResponseSchema>;

// Update FPL ID
export const UpdateFplIdRequestSchema = z.object({
  fpl_id: z.number().int().min(1).max(2147483647).nullable(),
});
export type UpdateFplIdRequest = z.infer<typeof UpdateFplIdRequestSchema>;

export const UpdateFplIdResponseSchema = z.object({
  success: z.literal(true),
  data: ProfileSchema,
  message: z.string().optional(),
});
export type UpdateFplIdResponse = z.infer<typeof UpdateFplIdResponseSchema>;

// Change Password
export const ChangePasswordRequestSchema = z.object({
  current_password: z.string().min(8).max(128),
  new_password: z.string().min(8).max(128),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const ChangePasswordResponseSchema = z.object({
  success: z.literal(true),
  message: z.string().optional(),
});
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

// Upload Avatar
export const UploadAvatarResponseSchema = z.object({
  success: z.literal(true),
  data: ProfileSchema,
  message: z.string().optional(),
});
export type UploadAvatarResponse = z.infer<typeof UploadAvatarResponseSchema>;

// Update Profile Settings
export const UpdateProfileSettingsRequestSchema = z.object({
  show_twitter_username: z.boolean().optional(),
  country: z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(), // ISO 3166-1 alpha-2
});
export type UpdateProfileSettingsRequest = z.infer<typeof UpdateProfileSettingsRequestSchema>;

export const UpdateProfileSettingsResponseSchema = z.object({
  success: z.literal(true),
  data: ProfileSchema,
  message: z.string().optional(),
});
export type UpdateProfileSettingsResponse = z.infer<typeof UpdateProfileSettingsResponseSchema>;

// Update UI flags (e.g. after showing Follow X modal or country modal)
export const UpdateUiFlagsRequestSchema = z.object({
  flag: z.union([
    z.literal('follow_x_modal_shown'),
    z.literal('country_modal_shown'),
    z.literal('revote.confirm.earlyBonus'),
    z.literal('revote.confirm.final'),
    z.literal('revote.confirm.finalWithEarlyBonus'),
  ]),
});
export type UpdateUiFlagsRequest = z.infer<typeof UpdateUiFlagsRequestSchema>;

export const UpdateUiFlagsResponseSchema = z.object({
  success: z.literal(true),
  data: ProfileSchema,
  message: z.string().optional(),
});
export type UpdateUiFlagsResponse = z.infer<typeof UpdateUiFlagsResponseSchema>;

